import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Calendar, Save, Zap, CheckCircle, AlertCircle, AlertTriangle, Loader2, Activity, Info, Play, RotateCcw, Timer, EyeOff } from 'lucide-react';
import ScheduleAnalysisModal from './network/ScheduleAnalysisModal';
import ProgramStartsEditor from './ProgramStartsEditor';
import espnowService, {
  type ProgramStart,
  type ScheduleProgram,
  type ScheduleStep,
  type StepRelay,
  type EspNowRouter,
  type EspNowController,
  type MasterPump,
  formatDuration,
  relayDisplayName,
  usableRelays,
} from '../../services/espnowIrrigation';
import { utcSecToLocalHHMM, localHHMMToUtcSec, getTodayDowInTz, formatDateTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { nodeState } from '../../utils/nodeState';
import { composeRelayDuration, splitRelayDuration } from '../../utils/relayDuration';
import {
  findScheduleConflicts,
  hasBlockingConflict,
  type ScheduleConflict,
  type ValveAddress,
} from '../../utils/scheduleConflicts';
import { programForStart, startsOf } from '../../utils/programStarts';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_VALUES = [1, 2, 3, 4, 5, 6, 7];

/** Run time a program falls back to when it has no valves to read one from. */
const DEFAULT_RUN_SEC = 300;

/** One valve, as the pickers identify it. A node alone is not enough — the
 *  same node drives several, and each is a separate block. */
function slotKey(routerMac: string, relayIndex: number): string {
  return `${routerMac.toUpperCase()}#${relayIndex}`;
}

/**
 * A program's starts in LOCAL time order - the order a grower reads a day in.
 * The server hands them back in UTC order, which on a +10 site puts a 9 AM
 * start (23:00 UTC) after the afternoon one.
 */
function startsInLocalOrder(program: ScheduleProgram, orgTimezone: string): ProgramStart[] {
  return [...startsOf(program)].sort((a, b) =>
    utcSecToLocalHHMM(a.start_time_sec, orgTimezone).localeCompare(
      utcSecToLocalHHMM(b.start_time_sec, orgTimezone)));
}

/** The program as the server sent it, with its starts in local order. */
function withLocalStarts(program: ScheduleProgram, orgTimezone: string): ScheduleProgram {
  return { ...program, starts: startsInLocalOrder(program, orgTimezone) };
}

/** What a save sends for the starts. */
function startsPayload(program: ScheduleProgram): ProgramStart[] {
  return (program.starts && program.starts.length > 0 ? program.starts : startsOf(program))
    .map(s => ({ id: s.id ?? null, start_time_sec: s.start_time_sec, duration_pct: s.duration_pct }));
}

/** Every valve duration in the program, in step then relay order. */
function relayDurations(program: ScheduleProgram): number[] {
  return (program.steps || []).flatMap(s => (s.relays || []).map(r => r.duration_sec));
}

interface EspNowScheduleBuilderProps {
  controller: EspNowController;
  /** Program to open on arrival — set when a link names one (see the farm page). */
  initialProgramId?: string | null;
  programs: ScheduleProgram[];
  routers: EspNowRouter[];
  masterPumps: MasterPump[];
  orgTimezone: string;
  onProgramsChange: (programs: ScheduleProgram[]) => void;
}

export default function EspNowScheduleBuilder({
  controller,
  initialProgramId = null,
  programs,
  routers,
  masterPumps,
  orgTimezone,
  onProgramsChange,
}: EspNowScheduleBuilderProps) {
  // XBee laptop sites hold their own timers. The server never dispatches to
  // them (see espnowSchedulerService.dispatchForController), so every control
  // and every word below that talks about 'the device' has to change.
  const isXBee = controller.transport === 'xbee';

  const [analysisProgramId, setAnalysisProgramId] = useState<string | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(() => {
    if (initialProgramId && programs.some(p => p.id === initialProgramId)) return initialProgramId;
    return programs.length > 0 ? programs[0].id : null;
  });

  // A link naming a program can arrive before the programs do, or change while
  // the tab is open. Only ever a program this controller actually has, so a
  // stale link falls back to the first one rather than showing nothing.
  useEffect(() => {
    if (initialProgramId && programs.some(p => p.id === initialProgramId)) {
      setSelectedProgramId(initialProgramId);
    }
  }, [initialProgramId, programs]);
  const [saving, setSaving] = useState(false);
  const [pushPhase, setPushPhase] = useState<'idle' | 'pushing' | 'awaiting_ack' | 'confirmed' | 'timeout' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  // Stored-but-inert schedules found after a push confirmed. Kept separate from
  // pushPhase so the warning outlives the transient success banner.
  const [armIssues, setArmIssues] = useState<string[]>([]);
  // Tracks asking the site to start, not the watering itself — the run is the
  // program's own schedule once it has begun. `awaiting_ack` is the state this
  // originally lacked, and its absence is what let the button report a start
  // the site had refused. See awaitRunAck.
  const [runPhase, setRunPhase] = useState<'idle' | 'running' | 'awaiting_ack' | 'started'>('idle');
  const [runNotice, setRunNotice] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollDeadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Whether each valve carries its own run time.
   *
   * null means nobody has said, and the program's own data answers: valves that
   * already share one duration open in uniform mode, mixed ones open showing
   * them. That way the screen can never claim a single run time over a program
   * that does not have one. Reset on every program change, because the answer
   * belongs to the program and not to the session.
   */
  const [perValveTimes, setPerValveTimes] = useState<boolean | null>(null);
  /**
   * Hide valves already placed in this program from the pickers. On by default:
   * building a twenty-valve program means walking a list that should be getting
   * shorter, not scanning the same full list twenty times for the one node not
   * yet used.
   */
  const [hideUsed, setHideUsed] = useState(true);
  const [applyingDuration, setApplyingDuration] = useState(false);
  const durationWriteRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (pollDeadlineRef.current) clearTimeout(pollDeadlineRef.current);
      if (durationWriteRef.current) clearTimeout(durationWriteRef.current);
    };
  }, []);

  const selectedProgram = programs.find(p => p.id === selectedProgramId) ?? null;

  // Starts arrive in UTC order, which on a +10 site lists a 9 AM start after
  // the afternoon one. Put the open program's into local order when it is
  // opened - and only then: re-sorting on every edit would move a row out from
  // under somebody typing a time into it.
  useEffect(() => {
    if (!selectedProgram) return;
    const sorted = startsInLocalOrder(selectedProgram, orgTimezone);
    const current = selectedProgram.starts ?? [];
    const inOrder = current.length === sorted.length
      && current.every((s, i) => s.start_time_sec === sorted[i].start_time_sec);
    if (!inOrder) {
      onProgramsChange(programs.map(p => p.id === selectedProgram.id ? { ...p, starts: sorted } : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on opening a program only; see above
  }, [selectedProgramId]);

  // ─── What the other programs on this controller do to this one ──────────────

  /**
   * Nothing refuses an overlapping pair — not the save routes, not the
   * dispatcher, not the bridge — so the only place it can be caught is in front
   * of the person writing it. Derived rather than validated on save, because
   * every control on this screen writes immediately: there is no one "save" to
   * hang a check off, and eight separate checks would drift apart.
   *
   * Recomputed on every program change, which is what makes moving a start time
   * clear the banner as you type rather than on some later reload.
   */
  const conflictsByProgram = useMemo(
    () => findScheduleConflicts(programs, {
      transport: controller.transport,
      masterPumps,
      orgTimezone,
    }),
    [programs, controller.transport, masterPumps, orgTimezone],
  );
  const selectedConflicts = selectedProgram
    ? conflictsByProgram.get(selectedProgram.id) ?? []
    : [];

  /**
   * Name one valve the way the rest of the app does: the label its node card
   * gives it, falling back to the node and relay index. A bare MAC in a warning
   * is a warning nobody can act on without opening two other pages.
   */
  function addressName(address: ValveAddress): string {
    const router = routers.find(
      r => r.router_mac.toUpperCase() === address.router_mac.toUpperCase(),
    );
    const label = relayDisplayName(null, router?.relay_labels, address.relay_index, '');
    if (label) return label;
    return `${router?.name || address.router_mac.slice(-8)} / Relay ${address.relay_index}`;
  }

  // ─── Run time: one for the sequence, or one per valve ───────────────────────

  const durations = selectedProgram ? relayDurations(selectedProgram) : [];
  const durationsDiffer = new Set(durations).size > 1;
  const perValve = perValveTimes ?? durationsDiffer;
  // What the single control shows. With no valves yet there is nothing to read,
  // so a sensible default stands in and the first valve added inherits it.
  const uniformDurationSec = durations.length > 0 ? Math.max(...durations) : DEFAULT_RUN_SEC;

  // ─── Which valves this program has already placed ──────────────────────────

  const usedSlots = new Set<string>();
  (selectedProgram?.steps || []).forEach(step =>
    (step.relays || []).forEach(r => usedSlots.add(slotKey(r.router_mac, r.relay_index)))
  );

  /**
   * Valves the program references that their node says are not in use.
   *
   * Worth saying out loud rather than quietly hiding: a relay marked out of use
   * that still sits in a program will still be driven, because the schedule row
   * is what the hardware runs. Silence here would leave a block watering with
   * nothing on the screen admitting it.
   */
  const scheduledButUnused: string[] = [];
  (selectedProgram?.steps || []).forEach(step =>
    (step.relays || []).forEach(r => {
      const node = routers.find(x => x.router_mac === r.router_mac);
      if (!node) return;
      if ((node.disabled_relays ?? []).includes(r.relay_index)) {
        const label = node.relay_labels?.[String(r.relay_index)] || `Relay ${r.relay_index}`;
        scheduledButUnused.push(`${node.name || node.router_mac.slice(-6)} · ${label}`);
      }
    })
  );

  /**
   * The first valve this program could add.
   *
   * Walks nodes in list order and takes the first relay that is in use and —
   * when the picker is hiding them — not already placed. Without this, "Add
   * Relay" lands on node 1 relay 0 every time, which under a hiding picker is
   * a row whose own selection is not in its own list.
   */
  function firstFreeSlot(skipUsed: boolean): { router_mac: string; relay_index: number } | null {
    for (const r of routers) {
      for (const i of usableRelays(r)) {
        if (skipUsed && usedSlots.has(slotKey(r.router_mac, i))) continue;
        return { router_mac: r.router_mac, relay_index: i };
      }
    }
    return null;
  }

  /**
   * Write one run time across every valve in the program.
   *
   * Optimistic locally and debounced to the server, because the control is a
   * pair of number boxes and a keystroke is not a decision — without the delay,
   * typing "45" writes 4 minutes to every valve on the way to 45.
   */
  function applyUniformDuration(seconds: number) {
    if (!selectedProgram) return;
    const safe = Math.max(1, Math.min(86400, seconds));
    const programId = selectedProgram.id;

    onProgramsChange(programs.map(p =>
      p.id === programId
        ? {
            ...p,
            steps: (p.steps || []).map(st => ({
              ...st,
              step_duration_sec: (st.relays || []).length > 0 ? safe : st.step_duration_sec,
              relays: (st.relays || []).map(r => ({ ...r, duration_sec: safe })),
            })),
          }
        : p
    ));

    if (durationWriteRef.current) clearTimeout(durationWriteRef.current);
    durationWriteRef.current = setTimeout(async () => {
      setApplyingDuration(true);
      try {
        const saved = await espnowService.setProgramRelayDuration(programId, safe);
        onProgramsChange(programs.map(p => p.id === saved.id ? saved : p));
      } catch {
        setError('Could not set the run time on every valve.');
      } finally {
        setApplyingDuration(false);
      }
    }, 600);
  }

  /**
   * Switching to one run time for the sequence rewrites durations somebody
   * chose, so it asks first. The longest wins: shortening a valve silently is
   * how a block ends up under-watered with nothing to show for it.
   */
  function handleTogglePerValve(next: boolean) {
    if (!next && durationsDiffer) {
      const longest = Math.max(...durations);
      if (!window.confirm(
        `This program's valves run for different lengths of time.

`
        + `Using one run time for the whole sequence will set every valve to ${formatDuration(longest)} — the longest of them.

`
        + `Switch back to separate times to undo it before saving.`
      )) return;
      applyUniformDuration(longest);
    }
    setPerValveTimes(next);
  }


  // ─── Program CRUD ───────────────────────────────────────────────────────────

  async function handleNewProgram() {
    setSaving(true);
    setError(null);
    try {
      const created = await espnowService.createProgram(controller.controller_id, {
        name: 'New Program',
        days_of_week: [1, 2, 3, 4, 5],
        starts: [{ start_time_sec: localHHMMToUtcSec('06:00', orgTimezone), duration_pct: 100 }],
        enabled: true,
      });
      // Add empty steps array so UI doesn't error
      const withSteps = { ...withLocalStarts(created, orgTimezone), steps: [] };
      const updated = [...programs, withSteps];
      onProgramsChange(updated);
      setSelectedProgramId(created.id);
    } catch {
      setError('Failed to create program');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteProgram(programId: string) {
    if (!confirm('Delete this program?')) return;
    try {
      await espnowService.deleteProgram(programId);
      const updated = programs.filter(p => p.id !== programId);
      onProgramsChange(updated);
      setSelectedProgramId(updated.length > 0 ? updated[0].id : null);
    } catch {
      setError('Failed to delete program');
    }
  }

  async function handleUpdateProgramField<K extends keyof ScheduleProgram>(
    field: K,
    value: ScheduleProgram[K]
  ) {
    if (!selectedProgram) return;
    const updated = { ...selectedProgram, [field]: value };
    // Optimistic update
    onProgramsChange(programs.map(p => p.id === selectedProgram.id ? updated : p));
  }

  async function handleSaveProgram() {
    if (!selectedProgram) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await espnowService.updateProgram(selectedProgram.id, {
        name: selectedProgram.name,
        days_of_week: selectedProgram.days_of_week,
        starts: startsPayload(selectedProgram),
        enabled: selectedProgram.enabled,
      });
      onProgramsChange(programs.map(p =>
        p.id === saved.id ? { ...withLocalStarts(saved, orgTimezone), steps: p.steps } : p));
    } catch (err) {
      // The server's own words: two starts too close together is refused with
      // a sentence saying which, and "Failed to save" would hide it.
      setError(errorMessage(err, 'Failed to save program'));
    } finally {
      setSaving(false);
    }
  }

  function stopPolling() {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (pollDeadlineRef.current) { clearTimeout(pollDeadlineRef.current); pollDeadlineRef.current = null; }
  }

  function resetPushPhase(after = 4000) {
    setTimeout(() => setPushPhase('idle'), after);
  }

  /**
   * A CONFIRMED ack only means the node *stored* the schedule. Ask the nodes
   * what they are actually holding and flag anything that will not water —
   * without this, a router with no clock reports as a clean success and the
   * block silently stays dry.
   */
  async function verifyArmed() {
    try {
      const nodes = await espnowService.getScheduleState(controller.controller_id);
      const issues: string[] = [];
      nodes.forEach(n => {
        const name = routers.find(r => r.router_mac === n.router_mac)?.name || n.router_mac.slice(-6);
        if (!n.clock_ok) {
          // Every relay on the node fails for this one reason — say it once.
          issues.push(`${name}: no time sync since boot — nothing on this node will run`);
          return;
        }
        n.relays
          .filter(r => r.enabled && !r.runnable)
          .forEach(r => {
            const label = routers.find(x => x.router_mac === n.router_mac)?.relay_labels?.[String(r.relay)]
              || `relay ${r.relay}`;
            issues.push(`${name} · ${label}: ${r.reason || 'stored but will not run'}`);
          });
      });
      setArmIssues(issues);
    } catch {
      // Endpoint unavailable — leave the plain confirmation standing rather
      // than inventing a warning we cannot substantiate.
      setArmIssues([]);
    }
  }

  async function handlePushSchedule() {
    if (!selectedProgram) return;
    setPushPhase('pushing');
    setError(null);
    setArmIssues([]);

    // ── 1. Save the program first ──────────────────────────────────────────────
    try {
      const saved = await espnowService.updateProgram(selectedProgram.id, {
        name: selectedProgram.name,
        days_of_week: selectedProgram.days_of_week,
        starts: startsPayload(selectedProgram),
        enabled: selectedProgram.enabled,
      });
      onProgramsChange(programs.map(p =>
        p.id === saved.id ? { ...withLocalStarts(saved, orgTimezone), steps: p.steps } : p));
    } catch (err) {
      setError(`${errorMessage(err, 'Failed to save program')} — push aborted.`);
      setPushPhase('error');
      resetPushPhase();
      return;
    }

    // ── 3. Push to device ──────────────────────────────────────────────────────
    // Capture timestamp before the HTTP call — if the call times out the server
    // may still have completed dispatch, so we still poll to confirm.
    const queuedAt = new Date().toISOString();
    try {
      await espnowService.pushSchedule(controller.controller_id);
    } catch {
      // Don't abort: a timeout means the server may have queued commands even
      // though the HTTP response never arrived. Polling will confirm either way.
    }

    setPushPhase('awaiting_ack');

    // ── 4. Poll for device ack every 2 seconds ─────────────────────────────────
    pollTimerRef.current = setInterval(async () => {
      try {
        const status = await espnowService.getPushAckStatus(controller.controller_id, queuedAt);
        if (status.total === 0) {
          // No commands queued (no programs enabled for today)
          stopPolling();
          setPushPhase('confirmed');
          resetPushPhase();
        } else if (status.error > 0) {
          stopPolling();
          setError('Device reported an error for one or more commands');
          setPushPhase('error');
          resetPushPhase();
        } else if (status.confirmed === status.total) {
          stopPolling();
          setPushPhase('confirmed');
          resetPushPhase();
          // Nodes post a fresh snapshot the moment a config is applied, so this
          // reads back the real armed/unarmed outcome rather than the ack alone.
          verifyArmed();
        }
        // else still waiting — keep polling
      } catch {
        // ignore transient poll errors
      }
    }, 2000);

    // Timeout after 60 seconds — commands queued but device hasn't confirmed yet
    pollDeadlineRef.current = setTimeout(() => {
      stopPolling();
      setPushPhase('timeout');
      resetPushPhase(6000);
    }, 60000);
  }

  // ─── Step CRUD ──────────────────────────────────────────────────────────────

  async function handleAddStep() {
    if (!selectedProgram) return;
    try {
      const step = await espnowService.addStep(selectedProgram.id);
      const withRelays: ScheduleStep = { ...step, relays: [] };
      onProgramsChange(programs.map(p =>
        p.id === selectedProgram.id
          ? { ...p, steps: [...(p.steps || []), withRelays] }
          : p
      ));
    } catch {
      setError('Failed to add step');
    }
  }

  async function handleDeleteStep(stepId: string) {
    if (!selectedProgram) return;
    try {
      await espnowService.deleteStep(stepId);
      onProgramsChange(programs.map(p =>
        p.id === selectedProgram.id
          ? { ...p, steps: (p.steps || []).filter(s => s.id !== stepId) }
          : p
      ));
    } catch {
      setError('Failed to delete step');
    }
  }

  async function handleMoveStep(stepId: string, direction: 'up' | 'down') {
    if (!selectedProgram) return;
    const steps = [...(selectedProgram.steps || [])].sort((a, b) => a.step_order - b.step_order);
    const idx = steps.findIndex(s => s.id === stepId);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= steps.length) return;

    // Swap step_order values
    const aOrder = steps[idx].step_order;
    const bOrder = steps[targetIdx].step_order;

    try {
      await Promise.all([
        espnowService.updateStep(steps[idx].id, { step_order: bOrder }),
        espnowService.updateStep(steps[targetIdx].id, { step_order: aOrder }),
      ]);
      const updated = steps.map(s => {
        if (s.id === steps[idx].id) return { ...s, step_order: bOrder };
        if (s.id === steps[targetIdx].id) return { ...s, step_order: aOrder };
        return s;
      });
      onProgramsChange(programs.map(p =>
        p.id === selectedProgram.id ? { ...p, steps: updated } : p
      ));
    } catch {
      setError('Failed to reorder steps');
    }
  }

  // ─── Relay CRUD ─────────────────────────────────────────────────────────────

  async function handleAddRelay(stepId: string) {
    if (!selectedProgram || routers.length === 0) return;
    const slot = firstFreeSlot(hideUsed);
    if (!slot) {
      setError(hideUsed
        ? 'Every relay in use on every node is already in this program. Turn off "Hide valves already used" to add a second run for one.'
        : 'No node here has a relay in use. Set which relays are in use on the node cards first.');
      return;
    }
    const defaultPumpId = masterPumps.length === 1 ? masterPumps[0].id : null;
    try {
      const relay = await espnowService.addRelay(stepId, {
        router_mac: slot.router_mac,
        relay_index: slot.relay_index,
        // A new valve joins the sequence at the sequence's run time. Landing on
        // a hardcoded 5 minutes would break the one claim uniform mode makes.
        duration_sec: perValve ? DEFAULT_RUN_SEC : uniformDurationSec,
        master_pump_id: defaultPumpId,
      });
      onProgramsChange(programs.map(p =>
        p.id === selectedProgram.id
          ? {
              ...p,
              steps: (p.steps || []).map(s =>
                s.id === stepId
                  ? { ...s, step_duration_sec: Math.max(s.step_duration_sec, relay.duration_sec), relays: [...(s.relays || []), relay] }
                  : s
              ),
            }
          : p
      ));
    } catch {
      setError('Failed to add relay');
    }
  }

  // Takes a patch rather than one field, because moving a relay to another node
  // can have to move its relay index too — the index that was free on the old
  // node may be in use or unwired on the new one, and sending those as two
  // writes leaves a moment where the row names a valve nobody chose.
  async function handleUpdateRelay(stepId: string, relayId: string, patch: Partial<StepRelay>) {
    if (!selectedProgram) return;
    // Optimistic update
    const updatedPrograms = programs.map(p =>
      p.id === selectedProgram.id
        ? {
            ...p,
            steps: (p.steps || []).map(s =>
              s.id === stepId
                ? { ...s, relays: (s.relays || []).map(r => r.id === relayId ? { ...r, ...patch } : r) }
                : s
            ),
          }
        : p
    );
    onProgramsChange(updatedPrograms);

    try {
      await espnowService.updateRelay(relayId, patch as Parameters<typeof espnowService.updateRelay>[1]);
      // Recalculate step duration from updated relay list
      const step = updatedPrograms.find(p => p.id === selectedProgram.id)?.steps?.find(s => s.id === stepId);
      if (step) {
        const maxDur = Math.max(...(step.relays || []).map(r => r.duration_sec), 0);
        onProgramsChange(updatedPrograms.map(p =>
          p.id === selectedProgram.id
            ? { ...p, steps: (p.steps || []).map(s => s.id === stepId ? { ...s, step_duration_sec: maxDur } : s) }
            : p
        ));
      }
    } catch (err) {
      // Put back what the server still holds. The optimistic write above has
      // already changed the run time on screen, and leaving it there after a
      // refusal means the builder shows a schedule the controller never
      // accepted — with nothing to distinguish it once the banner clears.
      onProgramsChange(programs);
      setError(errorMessage(err, 'Failed to update relay'));
    }
  }

  async function handleDeleteRelay(stepId: string, relayId: string) {
    if (!selectedProgram) return;
    try {
      await espnowService.deleteRelay(relayId);
      onProgramsChange(programs.map(p =>
        p.id === selectedProgram.id
          ? {
              ...p,
              steps: (p.steps || []).map(s =>
                s.id === stepId
                  ? { ...s, relays: (s.relays || []).filter(r => r.id !== relayId) }
                  : s
              ),
            }
          : p
      ));
    } catch {
      setError('Failed to delete relay');
    }
  }

  // ─── Calculated start times for display ────────────────────────────────────

  /**
   * When each step begins, once per start: index [step][start]. A start at a
   * shorter length reaches its later steps sooner, so each is timed from its
   * own scaled steps rather than by adding an offset to the first start.
   */
  function getStepStartTimes(program: ScheduleProgram): number[][] {
    const byStep: number[][] = [];
    for (const start of startsInLocalOrder(program, orgTimezone)) {
      const run = programForStart(program, start);
      const steps = [...(run.steps || [])].sort((a, b) => a.step_order - b.step_order);
      let offset = run.start_time_sec;
      steps.forEach((step, i) => {
        (byStep[i] ??= []).push(offset);
        offset += step.step_duration_sec;
      });
    }
    return byStep;
  }

  const startLabels = (starts: ProgramStart[]) =>
    starts.map(s => utcSecToLocalHHMM(s.start_time_sec, orgTimezone)).join(', ');

  function getProgramDurationSec(program: ScheduleProgram): number {
    return (program.steps || []).reduce((total, s) => total + (s.step_duration_sec || 0), 0);
  }

  const DAY_LABELS: Record<number, string> = {
    1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday',
    5: 'Friday', 6: 'Saturday', 7: 'Sunday',
  };

  // ─── Run now ────────────────────────────────────────────────────────────────

  /**
   * Wait for the site to say what it did with the start, and report THAT.
   *
   * Written after a forced run on an XBee site reported "Started" while not a
   * single valve opened. The API returning 200 only ever meant "the schedule
   * was moved and a command was queued" — the site had not been asked yet, let
   * alone answered. A bridge on a build predating RUN_PROGRAM acks it
   * `ERROR: unsupported command type 'RUN_PROGRAM'`, and that ack was sitting
   * in the database, correct and specific, while the screen said it was
   * watering. Nothing about the old code could tell the two apart, because it
   * never looked.
   *
   * So the success message now comes from the ack, not from the request. The
   * same poll the schedule push already uses, for the same reason: an
   * instruction queued is not an instruction carried out.
   */
  function awaitRunAck(queuedAt: string, staleNote: string) {
    setRunPhase('awaiting_ack');

    const finishOk = () => {
      setRunPhase('started');
      setRunNotice(
        (isXBee
          ? 'Started — the bridge confirmed it and is opening the first step now.'
          : 'Started — the controller confirmed the commands.') + staleNote
      );
      setTimeout(() => setRunPhase('idle'), 4000);
    };

    pollTimerRef.current = setInterval(async () => {
      try {
        const status = await espnowService.getPushAckStatus(controller.controller_id, queuedAt);
        // The site's own words, when it refused. Far more use than any sentence
        // this component could invent - "unsupported command type" says update
        // the bridge, and nothing else would have.
        const refusal = (status.details || []).find(d => d.status === 'ERROR');
        if (refusal) {
          stopPolling();
          setRunPhase('idle');
          setError(
            `The site refused the start: ${refusal.detail || 'no detail given'}`
            + (refusal.detail?.includes('unsupported command type')
              ? ' — this bridge is on a build that predates Run now. Update it from the controller page, then try again.'
              : '')
          );
          return;
        }
        if (status.total > 0 && status.confirmed >= status.total) {
          stopPolling();
          finishOk();
        }
        // else still waiting
      } catch {
        // Transient poll failure. The command stands either way; keep asking.
      }
    }, 2000);

    pollDeadlineRef.current = setTimeout(() => {
      stopPolling();
      setRunPhase('idle');
      // Deliberately not phrased as success. Nothing has confirmed anything,
      // and that is exactly the state the old code used to call "Started".
      setRunNotice(
        `The schedule was moved to now, but ${controller.controller_name || controller.controller_id} has not confirmed the start yet. `
        + `It collects commands every 15 seconds or so — if nothing happens, check the controller is online and its bridge is up to date.`
        + staleNote
      );
    }, 45000);
  }

  /**
   * Start this program immediately.
   *
   * The confirm text is long on purpose. This does NOT queue a one-off run —
   * the hardware cannot express "once", because a relay holds a single schedule
   * slot that it re-evaluates on time-of-day every day. So the server moves the
   * program's start time to now and the run happens because the program is
   * genuinely due. That is a persistent edit to a schedule somebody set
   * deliberately, and an operator has to be told before it happens, not
   * discover it a week later when the block waters at the wrong time.
   */
  async function handleRunNow() {
    if (!selectedProgram) return;

    const durationSec = getProgramDurationSec(selectedProgram);
    if (durationSec === 0) {
      setError('This program has no steps with relays, so there is nothing to run.');
      return;
    }

    const nowHHMM = utcSecToLocalHHMM(Math.floor(Date.now() / 1000) % 86400, orgTimezone);
    const currentStarts = startsInLocalOrder(selectedProgram, orgTimezone);
    const oldHHMM = startLabels(currentStarts);
    // The org's weekday, not the browser's — they differ either side of
    // midnight, and the server decides `day_added` from the org's calendar.
    const orgToday = getTodayDowInTz(orgTimezone);
    const addsDay = !selectedProgram.days_of_week.includes(orgToday);

    const lines = [
      `Run "${selectedProgram.name}" now? It will water for ${formatDuration(durationSec)}.`,
      '',
      currentStarts.length > 1
        // Every start goes, not just one: a node holds one entry per valve, and
        // a forced run sharing it with the next start would be cut short.
        ? `This replaces all ${currentStarts.length} of its start times (${oldHHMM}) with ${nowHHMM}${addsDay ? `, and adds ${DAY_LABELS[orgToday]} to its days` : ''}.`
        : `This moves the program's start time from ${oldHHMM} to ${nowHHMM}${addsDay ? `, and adds ${DAY_LABELS[orgToday]} to its days` : ''}.`,
      `It will keep starting at ${nowHHMM} until you change it — there is no such thing as a one-off run on this hardware.`,
      '',
      'You can put the old schedule back with the Restore button afterwards.',
    ];
    if (!selectedProgram.enabled) {
      // A disabled program cannot water, and an XBee bridge does not even sync
      // one — so running it necessarily switches it on. Restore puts that back
      // too, but nobody should find out by discovering it enabled next week.
      lines.splice(4, 0,
        'It will also be switched back on, because a disabled program cannot water.');
    }
    if (!controller.online) {
      // Worth interrupting for. On an XBee site the bridge refuses a start it
      // collects more than ten minutes late, so this is likely to do nothing at
      // all; on ESP-NOW the master will not see it until it reconnects. Either
      // way the outcome is "nothing happened", and working out why is not the
      // operator's job.
      lines.splice(1, 0, '',
        `WARNING: ${controller.controller_name || controller.controller_id} is offline right now, so this may not reach it. The schedule change is saved either way.`);
    }
    if (!window.confirm(lines.join('\n'))) return;

    setRunPhase('running');
    setError(null);
    setRunNotice(null);
    stopPolling();
    // Before the call: if it times out the server may still have queued the
    // command, and we would rather poll for an ack we cannot attribute than
    // miss one entirely.
    const queuedAt = new Date().toISOString();
    try {
      const result = await espnowService.runProgramNow(selectedProgram.id);
      // Keep our steps: the server returns the program tree but the local copy
      // may hold unsaved step edits, and silently discarding those on a button
      // that says "run" would be its own surprise.
      onProgramsChange(programs.map(p =>
        p.id === result.program.id ? { ...withLocalStarts(result.program, orgTimezone), steps: p.steps } : p
      ));
      // A relay that last reported ON, too long ago for the server to refuse the
      // run over. Not an error and not a countdown — nobody here can tell
      // whether that valve is open, and pretending otherwise either blocks the
      // button forever or hides a valve that really is running.
      const stale = result.unconfirmed ?? [];
      const staleNote = stale.length === 0
        ? ''
        : `\n\nWorth a look: ${stale.map(r => r.label).join(', ')} last reported ON, but nothing has confirmed that recently — it may or may not still be open.`;
      awaitRunAck(queuedAt, staleNote);
    } catch (err) {
      setRunPhase('idle');
      setError(errorMessage(err, 'Could not start the program.'));
    }
  }

  /** The start time(s) Restore would put back, for its button and prompt. */
  function restoreLabel(program: ScheduleProgram): string {
    const saved = program.previous_starts && program.previous_starts.length > 0
      ? program.previous_starts
      : [{ start_time_sec: program.previous_start_time_sec ?? 0, duration_pct: 100 }];
    return startLabels(startsInLocalOrder({ ...program, starts: saved }, orgTimezone));
  }

  async function handleRestoreSchedule() {
    if (!selectedProgram || selectedProgram.previous_start_time_sec == null) return;
    const backTo = restoreLabel(selectedProgram);
    if (!window.confirm(`Put "${selectedProgram.name}" back to starting at ${backTo}?`)) return;

    setRestoring(true);
    setError(null);
    setRunNotice(null);
    try {
      const restored = await espnowService.restoreProgramSchedule(selectedProgram.id);
      onProgramsChange(programs.map(p =>
        p.id === restored.id ? { ...withLocalStarts(restored, orgTimezone), steps: p.steps } : p
      ));
      setRunNotice(`Schedule restored — back to ${backTo}.`);
    } catch (err) {
      setError(errorMessage(err, 'Could not restore the schedule.'));
    } finally {
      setRestoring(false);
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4 md:flex-row h-full">
      {/* Program list (top strip on mobile, left panel on md+) */}
      <div className="flex-shrink-0 flex flex-col gap-2 md:w-52">
        <button
          onClick={handleNewProgram}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> New Program
        </button>

        <div className="flex flex-row gap-1.5 overflow-x-auto pb-1 md:flex-col md:overflow-x-visible md:overflow-y-auto md:gap-0 md:space-y-1 md:pb-0">
          {programs.map(p => (
            <button
              key={p.id}
              onClick={() => {
                setSelectedProgramId(p.id);
                // The run-time mode is a fact about the program, so the next
                // one gets to answer for itself rather than inherit this one's.
                setPerValveTimes(null);
              }}
              className={`flex-shrink-0 w-40 md:w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors ${
                selectedProgramId === p.id
                  ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between gap-1">
                <span className="truncate">{p.name}</span>
                {/* A clash belongs to a PAIR, so it has to be visible from the
                    program that is not open — otherwise the only way to find it
                    is to click through every program in the list. */}
                {hasBlockingConflict(conflictsByProgram.get(p.id)) ? (
                  <AlertTriangle
                    className="w-3.5 h-3.5 flex-shrink-0 text-red-500"
                    aria-label="Clashes with another program"
                  />
                ) : (
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${p.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                )}
              </div>
              <div className="text-xs text-gray-400 mt-0.5 truncate">
                {p.days_of_week.length === 7 ? 'Every day' : p.days_of_week.map(d => DAYS[d - 1]).join(', ')}
                {' · '}{startLabels(startsInLocalOrder(p, orgTimezone))}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Program editor (right panel) */}
      <div className="flex-1 overflow-y-auto">
        {!selectedProgram ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400">
            <Calendar className="w-10 h-10 mb-2 opacity-40" />
            <p className="text-sm">Select or create a program</p>
          </div>
        ) : (
          <div className="space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
            )}

            {/* What the other programs on this controller do to this one. Above
                the fields, because the fix is usually one of them — a start
                time, a day, or a valve that belongs to only one program. */}
            {selectedConflicts.map((conflict, i) => (
              <ConflictBanner
                key={`${conflict.kind}-${conflict.otherProgramId}-${i}`}
                conflict={conflict}
                isXBee={isXBee}
                addressName={addressName}
                onOpenOther={() => {
                  setSelectedProgramId(conflict.otherProgramId);
                  setPerValveTimes(null);
                }}
              />
            ))}

            {/* Program header fields */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Program Name</label>
                  <input
                    type="text"
                    value={selectedProgram.name}
                    onChange={e => handleUpdateProgramField('name', e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2 mt-5">
                  <span className="text-xs text-gray-500">{selectedProgram.enabled ? 'Enabled' : 'Disabled'}</span>
                  <button
                    onClick={() => handleUpdateProgramField('enabled', !selectedProgram.enabled)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                      selectedProgram.enabled ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${selectedProgram.enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>

              {/* Days of week */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Run on days</label>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {DAY_VALUES.map((d, i) => {
                    const isSelected = selectedProgram.days_of_week.includes(d);
                    return (
                      <button
                        key={d}
                        onClick={() => {
                          const days = isSelected
                            ? selectedProgram.days_of_week.filter(x => x !== d)
                            : [...selectedProgram.days_of_week, d];
                          handleUpdateProgramField('days_of_week', days.sort((a, b) => a - b));
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          isSelected
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                        }`}
                      >
                        {DAYS[i]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Start times - the same steps at each, each at its own length.
                  Edited locally and sent whole on save, like the days. */}
              <ProgramStartsEditor
                starts={selectedProgram.starts && selectedProgram.starts.length > 0
                  ? selectedProgram.starts
                  : startsOf(selectedProgram)}
                steps={selectedProgram.steps}
                orgTimezone={orgTimezone}
                onChange={starts => {
                  const earliest = Math.min(...starts.map(s => s.start_time_sec));
                  onProgramsChange(programs.map(p => p.id === selectedProgram.id
                    ? { ...selectedProgram, starts, start_time_sec: earliest }
                    : p));
                }}
              />

              {/* Run time — one for the whole sequence, or one per valve.
                  Uniform is the default because a twelve-step program is
                  almost always "this long each", and typing that twelve times
                  is the tedium, not the decision. */}
              <div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Timer className="w-3.5 h-3.5" /> Run time
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Separate time per valve</span>
                    <button
                      onClick={() => handleTogglePerValve(!perValve)}
                      role="switch"
                      aria-checked={perValve}
                      aria-label="Separate time per valve"
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                        perValve ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${perValve ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>

                {perValve ? (
                  <p className="text-xs text-gray-500 mt-1.5">
                    Each valve carries its own run time — set them on the relays below.
                  </p>
                ) : (
                  <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                    <input
                      type="number"
                      min={0}
                      max={1440}
                      aria-label="Run time minutes"
                      value={Math.floor(uniformDurationSec / 60)}
                      onChange={e => applyUniformDuration((parseInt(e.target.value) || 0) * 60 + (uniformDurationSec % 60))}
                      className="w-16 text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                    />
                    <span className="text-xs text-gray-400">m</span>
                    <input
                      type="number"
                      min={0}
                      max={59}
                      aria-label="Run time seconds"
                      value={uniformDurationSec % 60}
                      onChange={e => applyUniformDuration(Math.floor(uniformDurationSec / 60) * 60 + (parseInt(e.target.value) || 0))}
                      className="w-14 text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                    />
                    <span className="text-xs text-gray-400">s</span>
                    <span className="text-xs text-gray-500 ml-1">for every valve in this program</span>
                    {applyingDuration && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                  </div>
                )}
              </div>

              {/* Master pump banner */}
              {masterPumps.length > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                  <Zap className="w-4 h-4 flex-shrink-0 text-amber-500 mt-0.5" />
                  <div>
                    <span className="font-medium">{masterPumps.length === 1 ? 'Master pump' : 'Master pumps'}:</span>{' '}
                    {masterPumps.map((p, i) => {
                      const r = routers.find(x => x.router_mac === p.router_mac);
                      const name = p.label || `${r?.name || p.router_mac.slice(-8)} / Relay ${p.relay_index}`;
                      return <span key={p.id}>{i > 0 ? ', ' : ''}<strong>{name}</strong></span>;
                    })}
                    {masterPumps.length === 1
                      ? ' — auto-assigned to all relays'
                      : ' — assign per relay below'}
                  </div>
                </div>
              )}
            </div>

            {/* Steps */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h4 className="text-sm font-semibold text-gray-700">Steps (run sequentially)</h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <EyeOff className="w-3.5 h-3.5" /> Hide valves already used
                  </span>
                  <button
                    onClick={() => setHideUsed(v => !v)}
                    role="switch"
                    aria-checked={hideUsed}
                    aria-label="Hide valves already used"
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                      hideUsed ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${hideUsed ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>

              {/* A valve the program drives that its node says is wired to
                  nothing. Said out loud rather than hidden: the schedule row is
                  what the hardware runs, so marking the relay out of use does
                  not stop it watering. */}
              {scheduledButUnused.length > 0 && (
                <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <strong>
                      {scheduledButUnused.length === 1
                        ? 'One valve in this program is marked as not in use'
                        : `${scheduledButUnused.length} valves in this program are marked as not in use`}
                      :
                    </strong>{' '}
                    {scheduledButUnused.join(', ')}. It will still be driven — remove it here, or put
                    the relay back in use on the node card.
                  </div>
                </div>
              )}

              {(() => {
                const sortedSteps = [...(selectedProgram.steps || [])].sort((a, b) => a.step_order - b.step_order);
                const startTimes = getStepStartTimes(selectedProgram);

                return sortedSteps.map((step, idx) => (
                  <StepCard
                    key={step.id}
                    step={step}
                    stepNumber={idx + 1}
                    stepStartSecs={startTimes[idx] ?? []}
                    orgTimezone={orgTimezone}
                    isFirst={idx === 0}
                    isLast={idx === sortedSteps.length - 1}
                    routers={routers}
                    masterPumps={masterPumps}
                    perValve={perValve}
                    hideUsed={hideUsed}
                    usedSlots={usedSlots}
                    onMoveUp={() => handleMoveStep(step.id, 'up')}
                    onMoveDown={() => handleMoveStep(step.id, 'down')}
                    onDelete={() => handleDeleteStep(step.id)}
                    onAddRelay={() => handleAddRelay(step.id)}
                    onUpdateRelay={(relayId, patch) => handleUpdateRelay(step.id, relayId, patch)}
                    onDeleteRelay={(relayId) => handleDeleteRelay(step.id, relayId)}
                  />
                ));
              })()}

              <button
                onClick={handleAddStep}
                className="flex items-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Step
              </button>
            </div>

            {/* Save + Push buttons */}
            <div className="space-y-3 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleSaveProgram}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Save Program'}
                </button>
                {!isXBee && <button
                  onClick={handlePushSchedule}
                  disabled={pushPhase !== 'idle' || runPhase !== 'idle'}
                  className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg font-medium disabled:cursor-not-allowed transition-colors ${
                    pushPhase === 'confirmed' ? 'bg-green-600' :
                    pushPhase === 'error'     ? 'bg-red-600' :
                    pushPhase === 'timeout'   ? 'bg-amber-500' :
                                               'bg-green-600 hover:bg-green-700 disabled:opacity-75'
                  }`}
                >
                  {pushPhase === 'pushing' && <Loader2 className="w-4 h-4 animate-spin" />}
                  {pushPhase === 'awaiting_ack' && <Loader2 className="w-4 h-4 animate-spin" />}
                  {pushPhase === 'confirmed' && <CheckCircle className="w-4 h-4" />}
                  {pushPhase === 'error' && <AlertCircle className="w-4 h-4" />}
                  {pushPhase === 'timeout' && <AlertCircle className="w-4 h-4" />}
                  {pushPhase === 'idle' && <Zap className="w-4 h-4" />}
                  {pushPhase === 'idle'        && 'Save & Push to Device'}
                  {pushPhase === 'pushing'     && 'Sending…'}
                  {pushPhase === 'awaiting_ack'&& 'Waiting for device…'}
                  {pushPhase === 'confirmed'   && 'Device confirmed!'}
                  {pushPhase === 'error'       && 'Push failed'}
                  {pushPhase === 'timeout'     && 'Queued — device pending'}
                </button>}
                <button
                  onClick={handleRunNow}
                  // Also blocked mid-push: both share the one ack poller, and a
                  // push landing on top of a start would have each reading the
                  // other's acks.
                  disabled={runPhase !== 'idle' || pushPhase !== 'idle'}
                  className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg font-medium disabled:cursor-not-allowed transition-colors ${
                    runPhase === 'started'
                      ? 'bg-emerald-600'
                      : 'bg-amber-600 hover:bg-amber-700 disabled:opacity-75'
                  }`}
                  title="Start this program immediately by moving its start time to now"
                >
                  {runPhase === 'running'      && <Loader2 className="w-4 h-4 animate-spin" />}
                  {runPhase === 'awaiting_ack' && <Loader2 className="w-4 h-4 animate-spin" />}
                  {runPhase === 'started'      && <CheckCircle className="w-4 h-4" />}
                  {runPhase === 'idle'         && <Play className="w-4 h-4" />}
                  {runPhase === 'idle'         && 'Run now'}
                  {runPhase === 'running'      && 'Starting…'}
                  {/* Says what it is actually doing. "Started" here was the lie. */}
                  {runPhase === 'awaiting_ack' && (isXBee ? 'Waiting for the bridge…' : 'Waiting for the controller…')}
                  {runPhase === 'started'      && 'Started'}
                </button>
                <button
                  onClick={() => setAnalysisProgramId(selectedProgram.id)}
                  className="flex items-center gap-2 px-3 py-2 text-purple-700 hover:bg-purple-50 text-sm rounded-lg border border-purple-200"
                  title="Solve each step against the farm's irrigation network"
                >
                  <Activity className="w-4 h-4" /> Hydraulic check
                </button>
                <button
                  onClick={() => handleDeleteProgram(selectedProgram.id)}
                  className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 text-sm rounded-lg border border-red-200"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>

              {/* What "Run now" actually did, and the way back. This has to
                  survive a reload — an operator who forces a run on Friday and
                  comes back on Monday wondering why the block waters at 2pm
                  needs the answer on the screen, not in a toast they missed. */}
              {selectedProgram.previous_start_time_sec != null && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <strong>This program was started by hand.</strong>{' '}
                    Its start time was moved to{' '}
                    {utcSecToLocalHHMM(selectedProgram.start_time_sec, orgTimezone)} and it
                    will keep starting then
                    {selectedProgram.forced_run_at
                      ? ` (changed ${formatDateTimeInTz(selectedProgram.forced_run_at, orgTimezone)})`
                      : ''}.
                    <div className="mt-2">
                      <button
                        onClick={handleRestoreSchedule}
                        disabled={restoring}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-amber-100 border border-amber-300 rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        {restoring
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <RotateCcw className="w-3.5 h-3.5" />}
                        Restore {restoreLabel(selectedProgram)}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {runNotice && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
                  <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  {/* pre-line: the unconfirmed-relay warning is a second
                      paragraph, and without this it runs on into the first. */}
                  <span className="whitespace-pre-line">{runNotice}</span>
                </div>
              )}

              {/* An XBee site is told nothing; it asks. Saying so here is the
                  difference between "I saved it and nothing happened" and
                  knowing the laptop will have it within five minutes. */}
              {isXBee && (
                <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <strong>This is an XBee site, so there is nothing to push.</strong>{' '}
                    Saving is enough — the laptop bridge asks the server for its
                    programs about every 5 minutes and runs the timers itself, which
                    is what keeps the farm watering through an internet outage.
                  </div>
                </div>
              )}

              {analysisProgramId && (
                <ScheduleAnalysisModal
                  programId={analysisProgramId}
                  onClose={() => setAnalysisProgramId(null)}
                />
              )}

              {/* Push status banner */}
              {pushPhase === 'awaiting_ack' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                  <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                  <span>
                    Commands queued — waiting for device to collect and confirm…
                    {!controller.online && (
                      <span className="ml-1 text-amber-600">(Device shows as offline — it will pick up commands on reconnect.)</span>
                    )}
                  </span>
                </div>
              )}
              {pushPhase === 'confirmed' && armIssues.length === 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  Schedule stored and armed on every device.
                </div>
              )}
              {pushPhase === 'timeout' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  Commands queued but device has not yet confirmed. It will pick them up on next poll.
                </div>
              )}
              {/* Devices acked the write but reported back that it will not
                  water. Outlives the success banner — this is the failure the
                  operator would otherwise never see. */}
              {armIssues.length > 0 && (
                <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">
                      Stored, but {armIssues.length === 1 ? 'one relay will' : `${armIssues.length} relays will`} not run.
                    </p>
                    <ul className="mt-1 space-y-0.5 text-xs">
                      {armIssues.map((issue, i) => <li key={i}>{issue}</li>)}
                    </ul>
                  </div>
                  <button
                    onClick={() => setArmIssues([])}
                    className="text-amber-400 hover:text-amber-600 flex-shrink-0"
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Clash with another program ───────────────────────────────────────────────

interface ConflictBannerProps {
  conflict: ScheduleConflict;
  isXBee: boolean;
  addressName: (address: ValveAddress) => string;
  onOpenOther: () => void;
}

/**
 * One clash, said as what will happen rather than as "conflict detected".
 *
 * The two transports fail at an overlapping pair in different ways and have
 * different fixes, so the wording is not shared: telling an XBee operator to
 * combine two programs, or an ESP-NOW one to move a start time, is advice that
 * does not work. Same reason the severity differs — a shared valve is a program
 * that will not water on one transport and a timing question on the other.
 */
function ConflictBanner({ conflict, isXBee, addressName, onOpenOther }: ConflictBannerProps) {
  const isError = conflict.severity === 'error';
  const names = conflict.addresses.map(addressName).join(', ');
  const when = conflict.days.length >= 7
    ? ''
    : ` on ${conflict.days.map(d => DAYS[d - 1]).join(', ')}`;
  const overlap = conflict.overlapSec > 0 ? formatDuration(conflict.overlapSec) : null;
  const other = <strong>{conflict.otherProgramName}</strong>;

  let body: ReactNode;
  if (conflict.kind === 'shared-valve') {
    body = isXBee ? (
      <>
        {other} waters {names} at the same time as this program{when}, overlapping by {overlap}.
        The first of the two to reach a sequence change closes those valves on the other, which
        carries on believing they are still open.
      </>
    ) : (
      <>
        {other} waters {names} at the same time as this program{when}, overlapping by {overlap}.
        A node keeps one window per valve, so the two are merged: those valves stay open from the
        first start to the last finish, whatever either program&apos;s steps say.
      </>
    );
  } else if (conflict.kind === 'shared-pump') {
    body = isXBee ? (
      <>
        {other} draws on the same master pump ({names}) while this program is running{when}.
        Whichever cycle finishes first stops the pump, leaving the other watering with no
        pressure behind it.
      </>
    ) : (
      <>
        {other} draws on the same master pump ({names}) while this program is running{when}.
      </>
    );
  } else {
    body = (
      <>
        {other} runs at the same time as this program{when}, overlapping by {overlap}. They
        share no valves, so both will water — worth checking the line can supply them together.
      </>
    );
  }

  const fix = isError ? 'Move the start times apart so the programs take turns.' : null;

  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
        isError
          ? 'bg-red-50 border-red-200 text-red-800'
          : 'bg-amber-50 border-amber-200 text-amber-800'
      }`}
    >
      <AlertTriangle
        className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isError ? 'text-red-500' : 'text-amber-500'}`}
      />
      <div className="min-w-0 flex-1">
        <p>{body}</p>
        {fix && <p className="mt-1 font-medium">{fix}</p>}
        <button
          onClick={onOpenOther}
          className={`mt-1 text-xs font-semibold underline ${
            isError ? 'text-red-700 hover:text-red-900' : 'text-amber-700 hover:text-amber-900'
          }`}
        >
          Open {conflict.otherProgramName}
        </button>
      </div>
    </div>
  );
}

// ─── Step Card ────────────────────────────────────────────────────────────────

interface StepCardProps {
  step: ScheduleStep;
  stepNumber: number;
  /** When this step begins, once per start of the program. */
  stepStartSecs: number[];
  orgTimezone: string;
  isFirst: boolean;
  isLast: boolean;
  routers: EspNowRouter[];
  masterPumps: MasterPump[];
  /** False when one run time covers the whole program — the per-valve duration
   *  boxes come off, because they would be three copies of one number. */
  perValve: boolean;
  hideUsed: boolean;
  /** Every `slotKey` this program already drives, across all its steps. */
  usedSlots: Set<string>;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onAddRelay: () => void;
  onUpdateRelay: (relayId: string, patch: Partial<StepRelay>) => void;
  onDeleteRelay: (relayId: string) => void;
}

function StepCard({
  step,
  stepNumber,
  stepStartSecs,
  orgTimezone,
  isFirst,
  isLast,
  routers,
  masterPumps,
  perValve,
  hideUsed,
  usedSlots,
  onMoveUp,
  onMoveDown,
  onDelete,
  onAddRelay,
  onUpdateRelay,
  onDeleteRelay,
}: StepCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Step header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold">
            {stepNumber}
          </span>
          <span className="text-sm font-medium text-gray-700">
            Starts at {stepStartSecs.map(s => utcSecToLocalHHMM(s, orgTimezone)).join(', ')}
          </span>
          <span className="text-xs text-gray-400 hidden sm:inline">
            · {formatDuration(step.step_duration_sec)} duration
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move step up"
          >
            <ChevronUp className="w-4 h-4 text-gray-500" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move step down"
          >
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
            title="Delete step"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Relays in this step */}
      <div className="p-3 space-y-2">
        <p className="text-xs text-gray-400">Relays below run simultaneously during this step:</p>

        {(step.relays || []).map(relay => (
          <RelayRow
            key={relay.id}
            relay={relay}
            routers={routers}
            masterPumps={masterPumps}
            perValve={perValve}
            hideUsed={hideUsed}
            usedSlots={usedSlots}
            onUpdate={patch => onUpdateRelay(relay.id, patch)}
            onDelete={() => onDeleteRelay(relay.id)}
          />
        ))}

        <button
          onClick={onAddRelay}
          disabled={routers.length === 0}
          className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50 px-2 py-1.5 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Relay
        </button>
        {routers.length === 0 && (
          <p className="text-xs text-gray-400">No routers available — wait for device to connect.</p>
        )}
      </div>
    </div>
  );
}

// ─── Relay Row ────────────────────────────────────────────────────────────────

interface RelayRowProps {
  relay: StepRelay;
  routers: EspNowRouter[];
  masterPumps: MasterPump[];
  perValve: boolean;
  hideUsed: boolean;
  usedSlots: Set<string>;
  onUpdate: (patch: Partial<StepRelay>) => void;
  onDelete: () => void;
}

function RelayRow({ relay, routers, masterPumps, perValve, hideUsed, usedSlots, onUpdate, onDelete }: RelayRowProps) {
  const selectedRouter = routers.find(r => r.router_mac === relay.router_mac);
  const relayLabel = selectedRouter?.relay_labels?.[String(relay.relay_index)];

  const stored = splitRelayDuration(relay.duration_sec);
  // While a box is being typed in it holds its own text, because "" and "0"
  // are different states and only the stored value can settle the difference.
  // Nothing is sent until the pair composes a duration the API will take —
  // see utils/relayDuration.ts for the 400s that paid for this.
  const [draft, setDraft] = useState<{ min: string; sec: string } | null>(null);
  const shown = draft ?? { min: String(stored.min), sec: String(stored.sec) };

  function editDuration(next: { min: string; sec: string }) {
    setDraft(next);
    const total = composeRelayDuration(next.min, next.sec);
    if (total !== null) onUpdate({ duration_sec: total });
  }

  // Dropping the draft re-reads relay.duration_sec, so a box left empty snaps
  // back to what is actually saved rather than to a number nobody chose.
  const clearDraft = () => setDraft(null);

  const ownKey = slotKey(relay.router_mac, relay.relay_index);
  /** Relays this row could move to on a given node. Its own selection always
   *  survives the filter — a select whose value is not among its options shows
   *  blank, which reads as "nothing chosen" over a valve that is scheduled. */
  function offerableOn(node: EspNowRouter): number[] {
    return usableRelays(node).filter(i => {
      const key = slotKey(node.router_mac, i);
      return key === ownKey || !hideUsed || !usedSlots.has(key);
    });
  }

  const relayOptions = selectedRouter ? offerableOn(selectedRouter) : [];
  // Its own index even when the node now says that relay is out of use: the
  // schedule row is what the hardware runs, so it has to stay visible and
  // removable rather than silently reading as some other valve.
  if (!relayOptions.includes(relay.relay_index)) relayOptions.push(relay.relay_index);
  relayOptions.sort((a, b) => a - b);

  const routerOptions = routers.filter(r =>
    r.router_mac === relay.router_mac || offerableOn(r).length > 0
  );
  const hiddenNodes = routers.length - routerOptions.length;

  // Master pump label for the assigned pump
  const assignedPump = masterPumps.find(p => p.id === relay.master_pump_id);

  return (
    <div className="p-2.5 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
      {/* Row 1: Router select + delete */}
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <label className="text-xs text-gray-400 block mb-0.5">Router</label>
          <select
            value={relay.router_mac}
            onChange={e => {
              const next = routers.find(r => r.router_mac === e.target.value);
              const free = next ? offerableOn(next) : [];
              // Carry the index over only if the new node actually offers it;
              // otherwise take its first free relay, in the same write.
              const keep = free.includes(relay.relay_index);
              onUpdate({
                router_mac: e.target.value,
                ...(keep ? {} : free.length > 0 ? { relay_index: free[0] } : {}),
              });
            }}
            className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {routerOptions.map(r => (
              <option key={r.router_mac} value={r.router_mac}>
                {r.name || r.router_mac.slice(-8)}
                {nodeState(r) === 'offline' ? ' (did not answer)'
                  : nodeState(r) === 'unknown' ? ' (not verified)' : ''}
              </option>
            ))}
          </select>
          {hiddenNodes > 0 && (
            <div className="text-[11px] text-gray-400 mt-0.5">
              {hiddenNodes === 1 ? '1 node is' : `${hiddenNodes} nodes are`} fully used already
            </div>
          )}
        </div>
        <button
          onClick={onDelete}
          className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 flex-shrink-0 mt-4"
          title="Remove relay"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Row 2: Relay select + Duration */}
      <div className="flex items-end gap-2">
        {/* Relay index select */}
        <div className="flex-1 min-w-0">
          <label className="text-xs text-gray-400 block mb-0.5">Relay</label>
          <select
            value={relay.relay_index}
            onChange={e => onUpdate({ relay_index: parseInt(e.target.value) })}
            className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {relayOptions.map(i => {
              const label = selectedRouter?.relay_labels?.[String(i)];
              const outOfUse = (selectedRouter?.disabled_relays ?? []).includes(i);
              return (
                <option key={i} value={i}>
                  {label || `Relay ${i}`}{outOfUse ? ' — not in use' : ''}
                </option>
              );
            })}
          </select>
          {relayLabel && (
            <div className="text-xs text-blue-600 mt-0.5 truncate">{relayLabel}</div>
          )}
        </div>

        {/* Duration — only when each valve carries its own. Under one run time
            for the sequence these boxes would be N copies of one number, and
            editing any of them would silently make the program mixed. */}
        {perValve ? (
          <div className="flex-shrink-0">
            <label className="text-xs text-gray-400 block mb-0.5">Duration</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                max={1440}
                value={shown.min}
                onChange={e => editDuration({ ...shown, min: e.target.value })}
                onBlur={clearDraft}
                className="w-14 text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
              />
              <span className="text-xs text-gray-400">m</span>
              <input
                type="number"
                min={0}
                max={59}
                value={shown.sec}
                onChange={e => editDuration({ ...shown, sec: e.target.value })}
                onBlur={clearDraft}
                className="w-10 text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
              />
              <span className="text-xs text-gray-400">s</span>
            </div>
          </div>
        ) : (
          <div className="flex-shrink-0 pb-1">
            <span className="text-xs text-gray-400">{formatDuration(relay.duration_sec)}</span>
          </div>
        )}
      </div>

      {/* Row 3: Master pump selector (only when pumps are configured) */}
      {masterPumps.length > 0 && (
        <div>
          <label className="text-xs text-gray-400 block mb-0.5 flex items-center gap-1">
            <Zap className="w-3 h-3 text-amber-500" /> Master pump
          </label>
          {masterPumps.length === 1 ? (
            // Single pump — just show it as informational, no choice needed
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              {assignedPump
                ? (assignedPump.label || 'Master Pump')
                : <span className="text-gray-400 italic">None (auto-assigned at run time)</span>}
            </div>
          ) : (
            <select
              value={relay.master_pump_id ?? ''}
              onChange={e => onUpdate({ master_pump_id: e.target.value || null })}
              className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
            >
              <option value="">None</option>
              {masterPumps.map(p => (
                <option key={p.id} value={p.id}>
                  {p.label || `Pump ${masterPumps.indexOf(p) + 1}`}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
  );
}
